// Blocs partagés des Guides MILES — prerender-safe (pas de framer-motion, pas de window au rendu)
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, BookOpen, Building2, Clock, Compass, MapPinned, Plane, Route as RouteIcon, ShieldCheck, Users } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { CLUSTERS, GUIDES_COPY, aboutUrl, clusterUrl, formatDate, guideBookingHref, guideUrl, guidesInCluster } from "@/lib/guides";
import { lp } from "@/lib/seoContent";

export const CLUSTER_ICONS = { airports: Plane, routes: RouteIcon, cities: MapPinned, business: Building2, travellers: Users, miles: ShieldCheck };

export function GuideCard({ g, lang, large = false, onClick }) {
  const c = GUIDES_COPY[lang];
  const Icon = CLUSTER_ICONS[g.category] || Compass;
  return (
    <Link to={guideUrl(g)} onClick={onClick} data-testid={`guide-card-${g.slug}`}
      className="group flex h-full flex-col overflow-hidden rounded-3xl border border-ink/10 bg-white transition-[border-color,box-shadow] duration-300 hover:border-miles/40 hover:shadow-[0_24px_60px_-40px_rgba(36,87,255,0.45)]">
      {g.hero_image ? (
        <img src={g.hero_image} alt={g.hero_alt || ""} width={g.hero_width || 1600} height={g.hero_height || 900} loading="lazy" decoding="async"
          className={`w-full object-cover ${large ? "aspect-[16/9]" : "aspect-[16/10]"}`} />
      ) : (
        <div className={`flex items-center justify-center bg-miles-ice/60 ${large ? "aspect-[16/9]" : "aspect-[16/10]"}`}>
          <Icon className="h-9 w-9 text-miles/60" strokeWidth={1.25} />
        </div>
      )}
      <div className="flex flex-1 flex-col p-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-miles">{CLUSTERS[g.category]?.name[lang]}</p>
        <h3 className={`mt-2 font-bold leading-snug tracking-tight text-ink ${large ? "text-xl sm:text-2xl" : "text-base sm:text-lg"}`}>{g.title}</h3>
        {g.summary && <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-slate">{g.summary}</p>}
        <div className="mt-auto flex items-center justify-between pt-5 text-xs text-slate">
          <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" strokeWidth={1.5} />{formatDate(g.updated_at || g.published_at, lang)}</span>
          <span className="inline-flex items-center gap-1 font-semibold text-miles transition-transform group-hover:translate-x-0.5">{c.read} <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} /></span>
        </div>
      </div>
    </Link>
  );
}

export function ClusterCard({ id, lang }) {
  const cl = CLUSTERS[id];
  const Icon = CLUSTER_ICONS[id];
  const n = guidesInCluster(lang, id).length;
  const c = GUIDES_COPY[lang];
  return (
    <Link to={clusterUrl(lang, id)} data-testid={`cluster-card-${id}`}
      className="group flex flex-col rounded-3xl border border-ink/10 bg-white p-6 transition-[border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-miles/40">
      <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-miles-ice text-miles"><Icon className="h-5 w-5" strokeWidth={1.5} /></span>
      <h3 className="mt-5 text-base font-bold text-ink sm:text-lg">{cl.name[lang]}</h3>
      <p className="mt-2 flex-1 text-sm leading-relaxed text-slate">{cl.intro[lang]}</p>
      <p className="mt-5 flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-slate">
        <span>{c.guides_count(n)}</span><ArrowUpRight className="h-4 w-4 text-miles" strokeWidth={1.5} />
      </p>
    </Link>
  );
}

// CTA contextuel : réservation préremplie (trajet), réservation simple ou Business
export function GuideCta({ g, lang, position = "end" }) {
  const c = GUIDES_COPY[lang];
  if (g.cta_type === "none") return null;
  const isBusiness = g.cta_type === "business";
  const hasRoute = Boolean(g.booking_from || g.booking_to);
  const href = isBusiness ? lp(lang, "/business") : guideBookingHref(g);
  const label = g.cta_label || (isBusiness ? c.cta_business : hasRoute ? c.cta_booking : c.cta_book);
  const onClick = () => trackEvent("guide_cta_click", { guide_id: g.id, route_id: hasRoute ? `${g.booking_from}|${g.booking_to}` : null });
  return (
    <aside data-testid={`guide-cta-${position}`}
      className="not-prose my-10 overflow-hidden rounded-3xl bg-miles-deep p-7 text-white sm:p-9">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          {hasRoute && !isBusiness && (
            <p className="flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-miles-sky/80">
              <span>{g.booking_from || "…"}</span><ArrowRight className="h-3 w-3" /><span>{g.booking_to || "…"}</span>
            </p>
          )}
          <p className="mt-2 text-lg font-bold tracking-tight sm:text-xl">{label}</p>
          <p className="mt-1.5 text-sm text-white/70">{c.cta_hint}</p>
        </div>
        <Link to={href} onClick={onClick} data-testid={`guide-cta-link-${position}`}
          className="inline-flex h-12 shrink-0 items-center justify-center gap-2 rounded-full bg-white px-6 text-sm font-semibold text-miles-deep transition-opacity hover:opacity-90">
          {label} <ArrowRight className="h-4 w-4" strokeWidth={2} />
        </Link>
      </div>
    </aside>
  );
}

// module propriétaire « Ce trajet en bref » — données structurées du CMS, jamais inventées
export function GuideFacts({ g, lang }) {
  const facts = (g.facts || []).filter((f) => f.value?.trim());
  if (!facts.length) return null;
  return (
    <section data-testid="guide-facts" className="my-8 rounded-3xl border border-ink/10 bg-white p-6 sm:p-7">
      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-miles">{GUIDES_COPY[lang].facts_title}</p>
      <dl className="mt-4 grid gap-x-8 gap-y-4 sm:grid-cols-2">
        {facts.map((f) => (
          <div key={f.key} className="border-b border-ink/[0.06] pb-3">
            <dt className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate">{f.label}</dt>
            <dd className="mt-1 text-sm font-semibold text-ink">{f.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export function AuthorBlock({ lang, compact = false }) {
  const c = GUIDES_COPY[lang];
  return (
    <div className={`flex items-center gap-3 ${compact ? "" : "rounded-2xl border border-ink/10 bg-white p-4"}`} data-testid="guide-author">
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-miles-deep text-white"><BookOpen className="h-4 w-4" strokeWidth={1.5} /></span>
      <div className="min-w-0 text-sm">
        <p className="font-semibold text-ink">{c.author}</p>
        <p className="text-xs text-slate">{c.author_role} · <Link to={aboutUrl(lang)} className="text-miles hover:underline">{c.about_link}</Link></p>
      </div>
    </div>
  );
}
